import type { Role } from '../types/auth';
import { hasAnyRole, Policies } from '../auth/accessPolicies';

export interface NavItem {
  label: string;
  path: string;
  /** If empty/undefined: visible to any authenticated user. Otherwise visible if user has any of these roles. */
  roles?: readonly Role[];
  /** If set, the item is also hidden unless this feature key is in the enabled-features set. */
  feature?: string;
}

/**
 * Admin sidebar items. Each item is hidden (not just disabled) when the current user
 * has none of the listed roles. The plan calls these out as area homepages in 6.3.
 * AdminApp guards the matching routes with the same Policies, so a bookmarked or
 * post-login URL can't open an area the menu hides.
 */
export const adminNav: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    roles: Policies.questionWorkflowParticipant,
  },
  {
    label: 'Questions',
    path: '/questions',
    roles: Policies.questionWorkflowParticipant,
  },
  {
    label: 'Exam Sets',
    path: '/exam-sets',
    roles: Policies.administrator,
  },
  {
    label: 'Exams',
    path: '/exams',
    roles: Policies.administrator,
  },
  {
    label: 'Locations',
    path: '/locations',
    roles: Policies.examStaff,
  },
  {
    label: 'AI Generator',
    path: '/ai-generator',
    // Also gated by the tenant toggle.
    roles: Policies.administratorOrQuestionAuthor,
    feature: 'aiQuestionGeneration',
  },
  {
    label: 'Users',
    path: '/users',
    roles: Policies.administrator,
  },
  {
    label: 'Results',
    path: '/results',
    roles: Policies.administrator,
  },
  {
    label: 'Email Templates',
    path: '/email-templates',
    roles: Policies.administrator,
  },
  {
    label: 'Settings',
    path: '/settings',
    roles: Policies.administrator,
  },
];

export const candidateNav: NavItem[] = [
  { label: 'My Exams', path: '/exam-taking', roles: Policies.candidate },
];

export function visibleNav(
  items: NavItem[],
  userRoles: readonly string[],
  enabledFeatures?: ReadonlySet<string>,
): NavItem[] {
  return items.filter((item) => {
    if (item.feature && !enabledFeatures?.has(item.feature)) return false;
    if (!item.roles || item.roles.length === 0) return true;
    return hasAnyRole(userRoles, item.roles);
  });
}

/**
 * Where a user belongs when they have no specific destination (root URL, or a route they
 * lack access to): the first admin area they can see, else the candidate app. Null means the
 * account has no usable role at all — callers must render a dead end instead of redirecting,
 * or the bounce loops.
 */
export function homePathFor(userRoles: readonly string[]): string | null {
  const firstArea = visibleNav(adminNav, userRoles)[0];
  if (firstArea) return firstArea.path;
  if (hasAnyRole(userRoles, Policies.candidate)) return '/exam-taking';
  return null;
}
